import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { BusinessSelector } from '@/components/BusinessSelector'
import { DragDropZone, FileList } from '@/components/Upload'
import { useUpload } from '@/hooks'
import { useBusinessContext } from '@/context/BusinessContext'

export function UploadPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedBusinessId } = useBusinessContext()
  const [invalidFiles, setInvalidFiles] = useState<string[]>([])
  const [forceReprocess, setForceReprocess] = useState(false)

  const {
    pendingFiles,
    addFiles,
    removeFile,
    startUpload,
    clearCompleted,
    isUploading,
    completedCount,
    errorCount,
    duplicateCount,
    reprocessingCount,
    validatedCount,
  } = useUpload({
    businessId: selectedBusinessId || '',
    maxConcurrent: 3,
    forceReprocess,
  })

  const handleFilesSelected = (files: File[]) => {
    const result = addFiles(files)
    if (result.invalid.length > 0) {
      setInvalidFiles(result.invalid.map((f) => f.name))
      // Clear invalid files message after 5 seconds
      setTimeout(() => setInvalidFiles([]), 5000)
    }
  }

  // Consume files passed via router state from DocumentsPage drag-drop
  useEffect(() => {
    const stateFiles = (location.state as { files?: File[] })?.files
    if (stateFiles && stateFiles.length > 0) {
      handleFilesSelected(stateFiles)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async () => {
    await startUpload()
  }

  const handleViewDocuments = () => {
    navigate('/documents')
  }

  const pendingCount = pendingFiles.filter((f) => f.status === 'pending').length
  const hasFiles = pendingFiles.length > 0
  const canUpload = selectedBusinessId && pendingCount > 0 && !isUploading
  const showSuccess =
    (completedCount > 0 || reprocessingCount > 0 || duplicateCount > 0 || validatedCount > 0) &&
    !isUploading &&
    pendingCount === 0

  return (
    <div data-testid="upload-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Upload Documents</h1>
            <BusinessSelector />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Business selection prompt */}
        {!selectedBusinessId && (
          <div
            className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
            data-testid="business-prompt"
          >
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-yellow-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-sm text-yellow-700">
                Please select a business before uploading documents.
              </p>
            </div>
          </div>
        )}

        {/* Invalid files warning */}
        {invalidFiles.length > 0 && (
          <div
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
            data-testid="invalid-files-warning"
          >
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-red-600 mr-2 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-700">
                  The following files were rejected (unsupported file type):
                </p>
                <ul className="mt-1 text-sm text-red-600 list-disc list-inside">
                  {invalidFiles.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Success message */}
        {showSuccess && (
          <div
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
            data-testid="success-message"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-green-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <p className="text-sm text-green-700">
                  {completedCount} document{completedCount !== 1 ? 's' : ''} uploaded
                  successfully
                  {reprocessingCount > 0 && ` (${reprocessingCount} reprocessing)`}
                  {duplicateCount > 0 && ` (${duplicateCount} duplicate${duplicateCount !== 1 ? 's' : ''})`}
                  {validatedCount > 0 && ` (${validatedCount} already verified)`}
                  {errorCount > 0 && ` (${errorCount} failed)`}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleViewDocuments}
                  className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200"
                  data-testid="view-documents-button"
                >
                  View Documents
                </button>
                <button
                  type="button"
                  onClick={clearCompleted}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  data-testid="clear-completed-button"
                >
                  Upload More
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drag and drop zone */}
        <div className="mb-6">
          <DragDropZone
            onFilesSelected={handleFilesSelected}
            disabled={!selectedBusinessId || isUploading}
          />
        </div>

        {/* File list */}
        {hasFiles && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Selected Files ({pendingFiles.length})
              </h2>
              {pendingCount > 0 && (
                <span className="text-sm text-gray-500">
                  {pendingCount} pending
                </span>
              )}
            </div>
            <FileList files={pendingFiles} onRemoveFile={removeFile} />
          </div>
        )}

        {/* Upload button */}
        {hasFiles && (
          <div className="flex items-center justify-end gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={forceReprocess}
                onChange={(e) => setForceReprocess(e.target.checked)}
                data-testid="force-reprocess-checkbox"
              />
              Force reprocess
            </label>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!canUpload}
              className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                canUpload
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="upload-button"
            >
              {isUploading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Uploading...
                </span>
              ) : (
                `Upload ${pendingCount} File${pendingCount !== 1 ? 's' : ''}`
              )}
            </button>
          </div>
        )}
      </main>
    </div>
  )
}
